import { Checkbox, Input, Radio } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import {
  HARBOUR_ADOPTION_GAPS,
  HARBOUR_ADOPTION_GAP_META,
  HARBOUR_NO_PHONE_COPY,
  HARBOUR_OWNER_FILTERS,
  HARBOUR_OWNER_FILTER_META,
  HARBOUR_PLAN_FILTERS,
  HARBOUR_PLAN_FILTER_META,
  HARBOUR_SEARCH_GAP_MISS_COPY,
  HARBOUR_SEARCH_LIMIT,
  HARBOUR_SEARCH_MISS_ACTION,
  HARBOUR_SEARCH_MISS_COPY,
  getHarbourVenueDisplayName,
} from 'src/constants/harbour'
import { HARBOUR_TOOLTIPS } from 'src/utils/harbourTooltips'
import { hasHarbourPhone } from 'src/utils/harbourPhone'

/**
 * Harbour list filters — plan (primary), ownership, hide parked, has phone,
 * cheap search, paid adoption-gap chips. Search opens the drawer. It does not
 * turn Harbour into a Spartacus list. Has phone never applies to search.
 * Gap chips are session-only and search paid Harbour scope, not just top 30.
 */
const HarbourFilters = ({
  ownerFilter,
  planFilter,
  hideParked,
  hasPhone = false,
  search,
  adoptionGap = '',
  searchResults = [],
  searchLoading = false,
  disabled = false,
  onOwnerFilterChange,
  onPlanFilterChange,
  onHideParkedChange,
  onHasPhoneChange,
  onSearchChange,
  onAdoptionGapChange,
  onSelectVenue,
  onOpenSpartacus,
  searchMiss = false,
}) => {
  const hasQuery = String(search || '').trim().length > 0
  const hasGap = HARBOUR_ADOPTION_GAPS.includes(adoptionGap)
  const showResults =
    (hasQuery || hasGap) &&
    (searchLoading || searchResults.length > 0 || searchMiss)

  return (
    <Filters data-testid='harbour-filters'>
      <PlanFilterWrap
        data-testid='harbour-plan-filter'
        data-value={planFilter}
        title={HARBOUR_TOOLTIPS.planFilter}
      >
        <PlanGroup
          value={planFilter}
          onChange={event => onPlanFilterChange?.(event.target.value)}
          disabled={disabled}
          optionType='button'
          buttonStyle='solid'
          size='middle'
        >
          {HARBOUR_PLAN_FILTERS.map(key => (
            <Radio.Button
              key={key}
              value={key}
              data-testid={`harbour-plan-filter-${key}`}
            >
              {HARBOUR_PLAN_FILTER_META[key].label}
            </Radio.Button>
          ))}
        </PlanGroup>
      </PlanFilterWrap>

      <OwnerGroup
        value={ownerFilter}
        onChange={event => onOwnerFilterChange?.(event.target.value)}
        disabled={disabled}
        optionType='button'
        buttonStyle='solid'
        size='small'
        data-testid='harbour-owner-filter'
        title={HARBOUR_TOOLTIPS.ownerFilter}
      >
        {HARBOUR_OWNER_FILTERS.map(key => (
          <Radio.Button key={key} value={key}>
            {HARBOUR_OWNER_FILTER_META[key].label}
          </Radio.Button>
        ))}
      </OwnerGroup>

      <SearchWrap>
        <SearchInput
          allowClear
          disabled={disabled}
          placeholder={hasGap ? 'Narrow by name' : 'Find a venue'}
          title={HARBOUR_TOOLTIPS.search}
          value={search}
          onChange={event => onSearchChange?.(event.target.value)}
          onSearch={value => onSearchChange?.(value)}
          data-testid='harbour-search'
        />
        {showResults ? (
          <Results
            data-testid='harbour-search-results'
            aria-label={`Up to ${HARBOUR_SEARCH_LIMIT} venue matches`}
          >
            {searchLoading && searchResults.length === 0 ? (
              <EmptyHint>Searching…</EmptyHint>
            ) : null}
            {searchResults.map(venue => (
              <ResultButton
                key={venue.venueId}
                type='button'
                data-testid={`harbour-search-hit-${venue.venueId}`}
                onClick={() => onSelectVenue?.(venue)}
              >
                <span>{getHarbourVenueDisplayName(venue.venueName)}</span>
                {hasHarbourPhone(venue) ? (
                  <Phone>{venue.phoneNumber}</Phone>
                ) : (
                  <NoPhoneHit
                    data-testid={`harbour-search-no-phone-${venue.venueId}`}
                  >
                    {HARBOUR_NO_PHONE_COPY}
                  </NoPhoneHit>
                )}
              </ResultButton>
            ))}
            {!searchLoading && searchResults.length === 0 ? (
              <Miss data-testid='harbour-search-miss'>
                <MissCopy>
                  {hasGap && !hasQuery
                    ? HARBOUR_SEARCH_GAP_MISS_COPY
                    : HARBOUR_SEARCH_MISS_COPY}
                </MissCopy>
                {onOpenSpartacus && hasQuery ? (
                  <MissButton
                    type='button'
                    data-testid='harbour-open-spartacus'
                    onClick={() => onOpenSpartacus(search)}
                  >
                    {HARBOUR_SEARCH_MISS_ACTION}
                  </MissButton>
                ) : null}
              </Miss>
            ) : null}
          </Results>
        ) : null}
      </SearchWrap>

      <ChipRow>
        <HideParkedWrap title={HARBOUR_TOOLTIPS.hideParked}>
          <Checkbox
            checked={Boolean(hideParked)}
            disabled={disabled}
            onChange={event => onHideParkedChange?.(event.target.checked)}
            data-testid='harbour-hide-parked'
          >
            Hide parked
          </Checkbox>
        </HideParkedWrap>
        <HideParkedWrap title={HARBOUR_TOOLTIPS.hasPhone}>
          <Checkbox
            checked={Boolean(hasPhone)}
            disabled={disabled}
            onChange={event => onHasPhoneChange?.(event.target.checked)}
            data-testid='harbour-has-phone'
          >
            Has phone
          </Checkbox>
        </HideParkedWrap>
        {HARBOUR_ADOPTION_GAPS.map(gapKey => {
          const meta = HARBOUR_ADOPTION_GAP_META[gapKey]
          const pressed = adoptionGap === gapKey
          return (
            <GapChip
              key={gapKey}
              type='button'
              disabled={disabled}
              aria-pressed={pressed}
              $pressed={pressed}
              title={HARBOUR_TOOLTIPS.adoptionGap}
              data-testid={`harbour-adoption-gap-${gapKey}`}
              onClick={() => onAdoptionGapChange?.(pressed ? '' : gapKey)}
            >
              {meta.label}
            </GapChip>
          )
        })}
      </ChipRow>
    </Filters>
  )
}

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm || '8px'}
    ${({ theme }) => theme.spacing.md || '16px'};
`

const PlanFilterWrap = styled.div`
  flex: 0 0 auto;
`

const PlanGroup = styled(Radio.Group)`
  flex: 0 0 auto;
`

const OwnerGroup = styled(Radio.Group)`
  flex: 0 0 auto;
`

const SearchWrap = styled.div`
  position: relative;
  width: min(100%, 280px);
`

const SearchInput = styled(Input.Search)`
  width: 100%;
`

const Results = styled.div`
  position: absolute;
  z-index: 5;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 16rem;
  overflow: auto;
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 6px;
`

const ResultButton = styled.button`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus {
    background: #f4f8f6;
  }
`

const Phone = styled.span`
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  font-size: 12px;
  white-space: nowrap;
`

const NoPhoneHit = styled.span`
  color: #a94442;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`

const EmptyHint = styled.div`
  padding: 8px 10px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 12px;
`

const Miss = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px;
`

const MissCopy = styled.div`
  color: ${({ theme }) => theme.colours.stormGrey || '#555'};
  font-size: 12px;
`

const MissButton = styled.button`
  align-self: flex-start;
  padding: 4px 8px;
  border: 1px solid #d7e3dc;
  border-radius: 6px;
  background: #f4f8f6;
  color: #2d5a1e;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
`

const HideParkedWrap = styled.div`
  white-space: nowrap;
`

const GapChip = styled.button`
  margin: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colours.oliveGrey || '#c5cdc8'};
  background: ${({ $pressed }) => ($pressed ? '#5B8A72' : '#fff')};
  color: ${({ $pressed }) => ($pressed ? '#fff' : 'inherit')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export default HarbourFilters
